/**
 * Validates if a string contains valid HTML tags.
 * Returns true if the string contains properly formatted HTML tags,
 * false otherwise.
 *
 * @param str - The string to validate for HTML tags
 * @returns boolean - true if valid HTML tags are found, false otherwise
 */
export function isValidHtmlString(str: string): boolean {
  if (!str || typeof str !== 'string') {
    return false;
  }

  // Regular expression to match HTML tags
  // Matches opening tags like <tag>, <tag attr="value">, <tag/>
  // and closing tags like </tag>
  const htmlTagRegex =
    /<\/?[a-zA-Z][a-zA-Z0-9]*(\s+[a-zA-Z][a-zA-Z0-9]*(\s*=\s*(["'])[^"']*\3)?)*\s*\/?>/g;

  // Find all HTML tags in the string
  const matches = str.match(htmlTagRegex);

  if (!matches || matches.length === 0) {
    return false;
  }

  // Check if all tags are properly formatted
  for (const tag of matches) {
    // Basic validation: ensure tag starts with < and ends with >
    if (!tag.startsWith('<') || !tag.endsWith('>')) {
      return false;
    }

    // Check for self-closing tags or proper tag structure
    const isSelfClosing = tag.endsWith('/>');
    const isClosingTag = tag.startsWith('</');
    const isOpeningTag = tag.startsWith('<') && !tag.startsWith('</');

    if (!isSelfClosing && !isClosingTag && !isOpeningTag) {
      return false;
    }

    // Extract tag name and validate it
    const tagNameMatch = tag.match(/<\/?([a-zA-Z][a-zA-Z0-9]*)/);
    if (!tagNameMatch) {
      return false;
    }

    const tagName = tagNameMatch[1];

    // Check for valid HTML tag names (basic validation)
    if (!/^[a-zA-Z][a-zA-Z0-9]*$/.test(tagName)) {
      return false;
    }
  }

  return true;
}
